import { FC, ReactNode, createContext, useEffect } from "react";
import { omit } from "lodash";

import { ICart, ICartContext, ICartStorage } from "../types/cart";
import { IProductOffering } from "../types/order";
import usePersistentState from "../hooks/usePersistentState";

export const CartContext = createContext<ICartContext | null>(null);
const CART_SCHEMA_VERSION = 2;

const createEmptyCartStorage = (): ICartStorage => ({
  schemaVersion: CART_SCHEMA_VERSION,
  carts: {},
});

const isCartStorageV2 = (value: unknown): value is ICartStorage => {
  if (!value || typeof value !== "object") {
    return false;
  }

  const cartStorage = value as ICartStorage;
  return (
    cartStorage.schemaVersion === CART_SCHEMA_VERSION &&
    !!cartStorage.carts &&
    typeof cartStorage.carts === "object"
  );
};

const normalizeCartStorage = (
  value: unknown,
): { cart: ICartStorage; changed: boolean } => {
  if (isCartStorageV2(value)) {
    return { cart: value, changed: false };
  }

  // v1 (sem fairId) e dados inválidos são descartados por segurança.
  return { cart: createEmptyCartStorage(), changed: true };
};

interface Props {
  children: ReactNode;
}

const CartProvider: FC<Props> = ({ children }) => {
  const [storedCart, setStoredCart] = usePersistentState<unknown>(
    "cart",
    createEmptyCartStorage(),
  );
  const { cart, changed } = normalizeCartStorage(storedCart);

  useEffect(() => {
    if (changed) {
      setStoredCart(cart);
    }
  }, [cart, changed, setStoredCart]);

  const updateCart = (nextCart: ICartStorage) => {
    setStoredCart(nextCart);
  };

  const getCartBySlug = (slug?: string): ICart => {
    if (!slug) {
      return {};
    }

    return cart.carts[slug]?.items || {};
  };

  const syncCartFair = (slug?: string, fairId?: number): boolean => {
    if (!slug || !fairId) {
      return false;
    }

    const currentBucket = cart.carts[slug];

    if (!currentBucket) {
      updateCart({
        ...cart,
        carts: {
          ...cart.carts,
          [slug]: {
            fairId,
            items: {},
          },
        },
      });
      return false;
    }

    if (currentBucket.fairId !== fairId) {
      updateCart({
        ...cart,
        carts: {
          ...cart.carts,
          [slug]: {
            fairId,
            items: {},
          },
        },
      });
      return true;
    }

    return false;
  };

  const decreaseItem = (product: IProductOffering, slug: string) => {
    const slugCart = getCartBySlug(slug);
    if (slugCart[product.id]) {
      if (slugCart[product.id].amount - 1 <= 0) {
        updateCart({
          ...cart,
          carts: {
            ...cart.carts,
            [slug]: {
              fairId: cart.carts[slug].fairId,
              items: omit(slugCart, `${product.id}`) as ICart,
            },
          },
        });
      } else {
        updateCart({
          ...cart,
          carts: {
            ...cart.carts,
            [slug]: {
              fairId: cart.carts[slug].fairId,
              items: {
                ...slugCart,
                [product.id]: {
                  ...slugCart[product.id],
                  amount: slugCart[product.id].amount - 1,
                },
              },
            },
          },
        });
      }
    }
  };

  const getCart = (slug?: string) => getCartBySlug(slug);

  const increaseItem = (
    product: IProductOffering,
    slug: string,
    fairId?: number,
  ) => {
    if (!fairId) {
      return;
    }

    const currentBucket = cart.carts[slug];
    const bucketFairId = fairId;
    const fairChanged = !!currentBucket && currentBucket.fairId !== fairId;
    const slugCart = fairChanged ? {} : currentBucket?.items || {};

    if (slugCart[product.id]) {
      if (
        slugCart[product.id].amount < product.stock ||
        product.unlimited_stock
      ) {
        updateCart({
          ...cart,
          carts: {
            ...cart.carts,
            [slug]: {
              fairId: bucketFairId,
              items: {
                ...slugCart,
                [product.id]: {
                  ...slugCart[product.id],
                  amount: slugCart[product.id].amount + 1,
                },
              },
            },
          },
        });
      }
    } else {
      updateCart({
        ...cart,
        carts: {
          ...cart.carts,
          [slug]: {
            fairId: bucketFairId,
            items: {
              ...slugCart,
              [product.id]: {
                amount: 1,
                product,
                timestamp: Date.now(),
              },
            },
          },
        },
      });
    }
  };

  const emptyCart = (slug?: string) => {
    if (!slug) {
      updateCart(createEmptyCartStorage());
      return;
    }

    if (!cart.carts[slug]) {
      return;
    }

    updateCart({
      ...cart,
      carts: omit(cart.carts, slug) as ICartStorage["carts"],
    });
  };

  const removeItem = (product: IProductOffering, slug: string) => {
    const slugCart = getCartBySlug(slug);
    if (slugCart[product.id]) {
      updateCart({
        ...cart,
        carts: {
          ...cart.carts,
          [slug]: {
            fairId: cart.carts[slug].fairId,
            items: omit(slugCart, `${product.id}`) as ICart,
          },
        },
      });
    }
  };

  return (
    <CartContext.Provider
      value={{
        decreaseItem,
        emptyCart,
        getCart,
        increaseItem,
        removeItem,
        syncCartFair,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export default CartProvider;
